import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {WorklistService} from '../../services/worklist.service';
import {TablelistStoreHandlerService} from '../../services/tablelist-store-handler.service';
import {UserActionCreatorService} from '@ispm/user/services/user-action-creator.service';
import {ReviseService} from '@ispm/workflow/services/revise.service';
import {Subject} from 'rxjs';
import {ConfigurationService} from '../../../configuration/services/configuration.service';
import {filter, take} from 'rxjs/operators';
import {IRevision} from '@ispm/workflow/interfaces/revision.interface';
import {get} from 'lodash';
import {PermissionSchemaConstants} from '@ispm/shared/constants/permission-schema.constants';
import {EmrIntegrationService} from '@ispm/test/services/emr-integration.service';
import {WorklistHelper} from '../../worklist-helper';
import {PrefetchingService} from '@ispm/core/services/prefetching.service';
import {PatientService} from '@ispm-client/modules/patient/services/patient.service';
import {AuthenticationService} from '@ispm/user/services/authentication.service';
import {RouterManagerService} from "@ispm/core/services/router-manager.service";
import {ActivatedRoute, Router} from "@angular/router";
import {EMRIntegrationBeacon} from "@ispm-client/modules/pathways";

@Component({
  selector: 'gen-worklist-lean',
  templateUrl: './patient-dashboard.component.html',
  styleUrls: ['./patient-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientDashboardComponent implements OnInit, OnDestroy {
  public items;
  public loading: boolean = true;
  public patient = null;
  public patientId: string;
  public testListConfig = WorklistHelper.PATIENT_DASHBOARD_TEST;
  public values = [];

  private destroyed$: Subject<any> = new Subject();
  private org_id = null;

  constructor(
    private _changeDetector: ChangeDetectorRef,
    private _configurationService: ConfigurationService,
    private _userActionCreatorService: UserActionCreatorService,
    private authService: AuthenticationService,
    private emrIntegrationService: EmrIntegrationService,
    private patientService: PatientService,
    private prefetchingService: PrefetchingService,
    private routerManagerService: RouterManagerService,
    private tablelistStoreHandler: TablelistStoreHandlerService,
    private worklistService: WorklistService,
    private router: Router,
    private activatedRoute: ActivatedRoute
) {
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.loading = true;
    this.patientId = this.routerManagerService.parameters.patientId;
    this._configurationService.clearCachedConfigurations();
    this.patientService.get(this.patientId).pipe(take(1)).subscribe(res => {
      this.patient = res;
      this._userActionCreatorService.getCurrentUser().subscribe(user => {
        this.org_id = user.organization;
        this.reloadData();
      });
    });
  }

  goToTest() {
    this.tablelistStoreHandler.getConfigTemplateItem(this.testListConfig)
      .pipe(
        filter(config => !!config),
        take(1)
      )
      .subscribe((config: any) => {
        const testName = get(config, 'newTestConfig.payload.testName', 'Pathway Test');
        const emrIntegration = get(this.routerManagerService.parameters, 'EMRIntegration', '');

        const params = emrIntegration === EMRIntegrationBeacon ? {
          patientId: this.patientId,
          testName: testName,
          EMRIntegration: EMRIntegrationBeacon
        } : {
          patientId: this.patientId,
          testName: testName,
        };
        this.router.navigate(['order'], {relativeTo: this.activatedRoute});
      });
  }

  onRowClick(item) {
    const revisionId = item.data.lastRevision; // (item.data.activeRevision === 'None' ? item.data.lastRevision : item.data.activeRevision);
    const emrIntegration = this.emrIntegrationService.getEMRIntegration();

    this.worklistService.redirectToPatientProfileTest(this.patientId, item.data.workflowrequest_id, revisionId, emrIntegration);
  }

  isShowWarning() {
    const showWarning = this.routerManagerService.parameters.showWarning;

    return typeof showWarning === 'undefined';
  }

  userCanCreateTests() {
    return this.authService.doesUserHasPermission(PermissionSchemaConstants.shcemaDiagnosticOrderWrite);
  }

  private reloadData() {
    this.loading = true;
    this.tablelistStoreHandler.requestPatientTests(this.testListConfig, this.patientId, this.org_id)
      .subscribe(workflow => {
        if (workflow.workflows && workflow.workflows.length) {
          const promises = [];
          const revisions = [];
          workflow.workflows.forEach(item => {
            item.treatment = '';
            revisions.push(item.lastRevision);
          });
          const obs = this.tablelistStoreHandler.getSelectedTreatment(this.testListConfig, revisions.join()).publish();
          obs.connect();
          const promise = obs.toPromise();
          promise.then((data) => {
            if (data.length) {
              data.forEach(data => {
                if (data && data.priority && data.priority.length) {
                  const localItem = workflow.workflows.find(workflow => this.locateContextMatch(data, workflow));
                  if (localItem) {
                    localItem.treatment = data.priority[0].treatmentPlanName || data.priority[0].shortTitle;
                  }
                }
              });
            }
          });
          promises.push(promise);
          Promise.all(promises).then(() => this.updateView(workflow), () => this.updateView(workflow));
        } else {
          this.updateView(workflow);
        }
      });
  }

  private locateContextMatch(itemRev, workflow) {
    let item;
    const foundWorkflowRevision = function (ctx) {
      return (ctx.resourceType === 'WorkflowRevision' && ctx.reference === workflow.lastRevision);
    };
    if (itemRev.priority[0].context) {
      item = itemRev.priority[0].context.find((ctx => foundWorkflowRevision(ctx)));
    } else {
      if (itemRev.context.find((ctx => foundWorkflowRevision(ctx)))) {
        item = itemRev;
      }
    }
    return !!(item);
  }

  private async updateView(data) {
    this.values = data.workflows || []; // by pass for match correct requiremnt remove it if need tabs
    const promises = this.values.map(async item => {
      const revisionLike = {
          id: item.lastRevision,
          items: item.revItems.items,
          checkpoints: item.revCheckpoints
      } as Partial<IRevision>;
      item.revCheckpoints = ReviseService.filterAvailableCheckpoints([revisionLike] as any[], false);
      item.allowRevise = item.revCheckpoints.length > 0;

      const workflow = {
        id: item.workflowrequest_id,
        status: item.lastRevisionStatus,
        revisions: [revisionLike],
        activeRevision: item.activeRevision
      };
      const definitions = await this.checkTestConfiguration(item.testIntent);
      if (definitions) {
        item.readOnlyParameters = definitions.readOnlyMode;
        const templateRevise = ReviseService.getAvailableTemplates(definitions, workflow as any, revisionLike as any);
        item.revCheckpoints = [...item.revCheckpoints, ...templateRevise];
        item.allowRevise = item.revCheckpoints.length > 0;
        this._changeDetector.markForCheck();
      }
      return item;
    });
    this.values = await Promise.all(promises);
    this._userActionCreatorService.updatePatient(this.patientId, this.patient);
    this.loading = false;
    this._changeDetector.detectChanges();
    this.prefetchResourcesForNewTest();
  }

  private prefetchResourcesForNewTest(): void {
    window.setTimeout(() => {
      this.prefetchingService.prefetchOrganization();
      this.prefetchingService.prefetchTestCollections();
      this.prefetchingService.prefetchResourcesForTest();
    }, 500);
  }

  private checkTestConfiguration(testTypeId: string) {
    return this._configurationService.registerForConfiguration(testTypeId, 'test').toPromise();
  }
}
